import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Globe, Mountain, Palmtree, Compass, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Destinations = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [regions, setRegions] = useState({});
  const [activeRegion, setActiveRegion] = useState('north_central_america');
  const [loading, setLoading] = useState(true);

  const regionConfig = {
    north_central_america: {
      name: 'North & Central America',
      name_es: 'Norte y Centroamérica',
      icon: Mountain,
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-500',
      description: 'Mexico, Costa Rica, Guatemala and more',
      description_es: 'México, Costa Rica, Guatemala y más'
    },
    south_america: {
      name: 'South America',
      name_es: 'Sudamérica',
      icon: Compass,
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-500',
      description: 'Peru, Brazil, Patagonia and beyond',
      description_es: 'Perú, Brasil, Patagonia y más'
    },
    caribbean: {
      name: 'Caribbean',
      name_es: 'Caribe',
      icon: Palmtree,
      color: 'from-cyan-500 to-blue-600',
      bgColor: 'bg-cyan-500',
      description: 'Dominican Republic, Cuba and island vibes',
      description_es: 'República Dominicana, Cuba y vibras isleñas'
    }
  };

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/destinations/by-region?lang=${language}`);
        const data = await response.json();
        setRegions(data.regions || {});
      } catch (error) {
        console.error('Error fetching destinations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, [language]);

  const getVibeEmoji = (vibe) => {
    const vibeEmojis = {
      adventure: '🏔️',
      culture: '🎭',
      relaxation: '🌴'
    };
    return vibeEmojis[vibe] || '✨';
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="destinations-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block px-4 py-2 bg-[#84bc00]/20 text-[#84bc00] text-sm font-bold rounded-full mb-4">
            {language === 'es' ? 'EXPLORA POR REGIÓN' : 'EXPLORE BY REGION'}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4">
            {language === 'es' ? 'Destinos' : 'Destinations'}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Descubre los lugares más increíbles de América Latina y el Caribe' 
              : 'Discover the most incredible places in Latin America & the Caribbean'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Region Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12" data-testid="region-tabs">
          {Object.entries(regionConfig).map(([key, config]) => {
            const IconComponent = config.icon;
            const isActive = activeRegion === key;
            const destinationCount = regions[key]?.destinations?.length || 0;
            
            return (
              <button
                key={key}
                data-testid={`region-tab-${key}`}
                onClick={() => setActiveRegion(key)}
                className={`
                  flex items-center gap-3 px-6 py-4 rounded-xl font-bold transition-all duration-300
                  ${isActive 
                    ? `bg-gradient-to-r ${config.color} text-white shadow-lg scale-105` 
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'}
                `}
              >
                <IconComponent className="w-5 h-5" />
                <span>{language === 'es' ? config.name_es : config.name}</span>
                <span className={`
                  px-2 py-0.5 rounded-full text-xs font-bold
                  ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}
                `}>
                  {destinationCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active Region Header */}
        {regionConfig[activeRegion] && (
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">
              {language === 'es' ? regionConfig[activeRegion].name_es : regionConfig[activeRegion].name}
            </h2>
            <p className="text-gray-500">
              {language === 'es' ? regionConfig[activeRegion].description_es : regionConfig[activeRegion].description}
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-gray-200 rounded-2xl h-96"></div>
            ))}
          </div>
        ) : (
          /* Destinations Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regions[activeRegion]?.destinations?.map((dest) => (
              <div
                key={dest.id}
                data-testid={`destination-card-${dest.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={dest.image}
                    alt={dest.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  
                  {/* Country Badge */}
                  <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-full ${regionConfig[activeRegion]?.bgColor} text-white text-xs font-bold flex items-center gap-1.5`}>
                    <Globe className="w-3.5 h-3.5" />
                    {dest.country}
                  </div>

                  {/* Destination Name on Image */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-black text-white mb-1">
                      {dest.name}
                    </h3>
                    {/* Vibes */}
                    <div className="flex gap-2">
                      {dest.vibes?.map((vibe, i) => (
                        <span key={i} className="text-white/80 text-sm">
                          {getVibeEmoji(vibe)} {vibe}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {dest.description}
                  </p>

                  {/* Activities */}
                  {dest.activities && dest.activities.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-bold text-gray-400 uppercase mb-2">
                        {language === 'es' ? 'Actividades' : 'Activities'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {dest.activities.slice(0, 4).map((activity, i) => (
                          <span 
                            key={i}
                            className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full"
                          >
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Logistics */}
                  {dest.logistics && (
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-4 pt-4 border-t border-gray-100">
                      {dest.logistics.currency && (
                        <span>💰 {dest.logistics.currency}</span>
                      )}
                      {dest.logistics.language && (
                        <span>🗣️ {dest.logistics.language}</span>
                      )}
                      {dest.logistics.best_time && (
                        <span>📅 {dest.logistics.best_time}</span>
                      )}
                    </div>
                  )}

                  {/* CTA */}
                  <Button
                    onClick={() => navigate(`/destinations/${dest.id}`)}
                    className={`w-full bg-gradient-to-r ${regionConfig[activeRegion]?.color} hover:opacity-90 text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center gap-2`}
                  >
                    {language === 'es' ? 'Explorar' : 'Explore'}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && (!regions[activeRegion]?.destinations || regions[activeRegion].destinations.length === 0) && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🗺️</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'es' ? 'No hay destinos en esta región' : 'No destinations in this region'}
            </h3>
            <p className="text-gray-500">
              {language === 'es' 
                ? 'Pronto agregaremos más destinos increíbles' 
                : 'We\'ll be adding more incredible destinations soon'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Destinations;
